import ServiceBase from './ServiceBase'

/**
 * Affiliate "Cash Back" program API client.
 * Wraps the backend JSON endpoints in web/controller/api/affiliate.php.
 * All calls use webPost (form-encoded body) so the PHP `request->post` /
 * `get_post` populate; the backend reply is `{ success, data?, error?, msg?, errors? }`.
 */

export interface AffiliateStatus {
  is_affiliate: number
  affiliate_id: number
  code: string
  approved: number
  approval_required: number
  commission: number
  module_enabled: number
}

export interface AffiliateProfile {
  affiliate_id: number
  code: string
  address_1: string
  address_2: string
  city: string
  postcode: string
  country_id: number
  zone_id: number
  tax: string
  payment: string
  cheque: string
  paypal: string
  bank_name: string
  bank_branch_number: string
  bank_swift_code: string
  bank_account_name: string
  bank_account_number: string
  commission: number
  approved: number
  date_added: string
}

export interface AffiliateTransaction {
  amount: string
  amount_raw: number
  product_id: number
  description: string
  date_added: string
  active_date: string
  status: string
  status_text: string
  payout_info: string
}

export interface AffiliateApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  msg?: string
  errors?: Record<string, string>
}

class AffiliateService extends ServiceBase {
  private call<T = any>(
    route: string,
    params: Record<string, any> = {}
  ): Promise<AffiliateApiResponse<T>> {
    return new Promise((resolve, reject) => {
      this.webPost(
        params,
        (res: any) => resolve(res),
        route,
        (error: any) => reject(error)
      )
    })
  }

  /** Is the affiliate (cash-back) module switched on? Lightweight, no auth. */
  getModuleStatus() {
    return this.call<{ enabled: number }>('api/affiliate/moduleStatus')
  }

  /** Is the logged-in customer an affiliate? (+ approval state, commission rate) */
  getStatus() {
    return this.call<AffiliateStatus>('api/affiliate/status')
  }

  /** Full affiliate record for the edit form. */
  getProfile() {
    return this.call<AffiliateProfile>('api/affiliate/profile')
  }

  /** Join the cash-back program. */
  register(data: Record<string, any>) {
    return this.call('api/affiliate/register', data)
  }

  /** Update affiliate profile (address + payout). */
  edit(data: Record<string, any>) {
    return this.call('api/affiliate/edit', data)
  }

  /** Update payout method only. */
  savePayment(data: Record<string, any>) {
    return this.call('api/affiliate/savePayment', data)
  }

  /** Commission history + balance. */
  getTransactions(page = 1, limit = 10) {
    return this.call('api/affiliate/transactions', { page, limit })
  }

  /** Affiliate-eligible products for the tracking-link dropdown. */
  getProducts() {
    return this.call('api/affiliate/products')
  }

  /** Type-ahead trip search (by name) for the tracking-link dropdown. */
  searchProducts(keyword: string) {
    return this.call<{
      products: Array<{
        product_id: number
        name: string
        model: string
        keyword: string
        tour_code: number
      }>
    }>('api/affiliate/searchProducts', { keyword })
  }

  /** Build a referral link for a product. */
  getTrackingCode(productId: number | string) {
    return this.call<{ url: string; code: string }>('api/affiliate/trackingCode', {
      product_id: productId
    })
  }

  /** Per-trip commission rate (as configured in the backend rule). */
  getCommissionRate(productId: number | string) {
    return this.call<{ has_rate: number; type?: string; amount?: number; display?: string }>(
      'api/affiliate/commissionRate',
      { product_id: productId }
    )
  }

  /** Program-terms link (catalog/information article) for the register agree box. */
  getTerms() {
    return this.call<{ html: string; title: string; url: string; information_id: number }>(
      'api/affiliate/terms'
    )
  }

  /** Raw HTML of a CMS information article, for the terms popup (same source as payment). */
  getInformationContent(informationId: number | string) {
    return new Promise<{ data?: string }>((resolve, reject) => {
      this.webGet(
        {},
        (res: any) => resolve(res),
        'information/information/agree&information_id=' + informationId,
        (error: any) => reject(error)
      )
    })
  }

  /** Country list for the register form. */
  getCountries() {
    return this.call('api/affiliate/countries')
  }

  /** Zones for a country (+ postcode_required). */
  getZones(countryId: number | string) {
    return this.call('api/affiliate/zones', { country_id: countryId })
  }
}

export default new AffiliateService()
